import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { Box, Typography, Alert, Stack, Paper, CardContent } from "@mui/material";
import { useSingleProfileLookupMutation } from "../../../services/ProfileService";
import { ProfileView } from "../../../services/ProfileService/types/profileTypes";
import { PageLoading as Loading } from '../../common/components';
import { PageCard } from "../../common/components/styled-ui-elements";
import { ProfileInfoSection } from "../components/sections/ProfileInfoSection";
import { HeaderSection } from "../components/sections/HeaderSection";
import { CenterAccessSection } from "../components/sections/CenterAccessSection";
import { ClaimsAccessSection } from "../components/sections/ClaimsAccessSection";
import { useProfileData } from "../hooks/useProfileData";
import { THEME_COLORS, transformClaimsAccess, transformAvailableCenters, transformAvailableClaims } from "../utils/profileUtils";
import { useGetAvailableClaimsQuery, useGetMyProfileQuery } from "../../../services/ProfileService";
import { useGetCentersQuery } from "../../../services/ConfigurationService";

// Header Component using HeaderSection
const PageHeader: React.FC<{ profile: ProfileView | undefined; userName: string; onBack: () => void }> = ({ profile, userName, onBack }) => (
  <Paper elevation={1} sx={{ p: 1, mb: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <HeaderSection 
        profile={profile} 
        userName={userName} 
        onBack={onBack} 
      />
    </Stack>
  </Paper>
);

// A self-created profile has no organization yet, so fall back to the signed-in admin's.
const centerListCompany = (target?: ProfileView, admin?: ProfileView): string =>
  target?.organization || admin?.organization || "";

const ProfileEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userName = searchParams.get('userName');
  
  const [lookupProfile, { data: profile, isLoading, error }] = useSingleProfileLookupMutation();
  const [role, setRole] = useState("");
  
  // Initialize profile lookup when component mounts or userName changes
  useEffect(() => {
    if (userName) {
      lookupProfile({ identity: userName });
    }
  }, [userName, lookupProfile]);

  // Initialize role when profile loads
  useEffect(() => {
    if (profile?.role) setRole(profile.role);
  }, [profile?.role]);

  // Get additional data needed for transforms
  const { data: availableClaimsData } = useGetAvailableClaimsQuery();
  const { data: myProfile } = useGetMyProfileQuery();
  const centersCompany = centerListCompany(profile, myProfile);
  const { data: availableCentersData } = useGetCentersQuery({ company: centersCompany }, { skip: !centersCompany });

  // Transform data using shared utilities
  const transformedClaimsAccess = profile ? transformClaimsAccess(profile.claimsAccess) : [];
  const transformedAvailableClaims = transformAvailableClaims(availableClaimsData);
  const transformedAvailableCenters = transformAvailableCenters(availableCentersData);

  // Use shared profile data hook for handlers
  const {
    handleRoleChange: baseHandleRoleChange,
    handleAddClaimAccess: baseHandleAddClaimAccess,
    handleDeleteClaimAccess: baseHandleDeleteClaimAccess,
    handleAddCenterAccess: baseHandleAddCenterAccess,
    handleDeleteCenterAccess: baseHandleDeleteCenterAccess
  } = useProfileData({ 
    profile, 
    refreshProfile: () => lookupProfile({ identity: userName! }) 
  });

  // Custom role change handler that refreshes profile data
  const handleRoleChange = async (newRole: string) => {
    setRole(newRole);
    await baseHandleRoleChange(newRole);
  };

  // Custom handlers that refresh profile data after mutations
  const handleAddClaim = async (claim: { value: string; name: string }) => {
    await baseHandleAddClaimAccess(claim);
  };

  const handleDeleteClaim = async (claim: { value: string; name: string }) => {
    await baseHandleDeleteClaimAccess(claim);
  };

  const handleAddCenter = async (center: { code: string; name: string }) => {
    await baseHandleAddCenterAccess(center);
  };

  const handleDeleteCenter = async (center: { code: string; name: string }) => {
    await baseHandleDeleteCenterAccess(center);
  };

  const handleBack = () => {
    navigate('/my-access');
  };

  // Handle missing userName parameter
  if (!userName) {
    return (
      <AuthenticatedTemplate>
        <Box sx={{ 
          minHeight: '100vh',
          backgroundColor: THEME_COLORS.background,
          py: 1,
          px: 1.5
        }}>
          <Box display="flex" justifyContent="center">
            <PageCard>
              <Alert severity="error">
                No userName parameter provided. Please navigate from a valid profile link.
              </Alert>
            </PageCard>
          </Box>
        </Box>
      </AuthenticatedTemplate>
    );
  }

  if (isLoading) return <Loading />;
  if (error) return (
    <Alert severity="error" sx={{ m: 2 }}>
      Failed to load profile for "{userName}". Please try again or verify the user exists.
    </Alert>
  );

  return (
    <AuthenticatedTemplate>
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: THEME_COLORS.background,
        py: 1,
        px: 1.5
      }}>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <PageHeader 
              profile={profile} 
              userName={userName} 
              onBack={handleBack} 
            />
            
            <CardContent sx={{ p: 2, backgroundColor: THEME_COLORS.card }}>
              {profile && (
                <Stack spacing={2}>
                  {/* Profile Information */}
                  <ProfileInfoSection 
                    profile={profile} 
                    role={role} 
                    onRoleChange={handleRoleChange}
                  />

                  {/* Center Access and Claims Access - Side by Side */}
                  <Stack direction="row" spacing={2}>
                    <CenterAccessSection
                      centers={profile?.centerAccess || []}
                      availableCenters={transformedAvailableCenters}
                      onAddCenter={handleAddCenter}
                      onDeleteCenter={handleDeleteCenter}
                    />
                    <ClaimsAccessSection
                      claims={transformedClaimsAccess}
                      availableClaims={transformedAvailableClaims}
                      onAddClaim={handleAddClaim}
                      onDeleteClaim={handleDeleteClaim}
                    />
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </PageCard>
        </Box>
      </Box>
    </AuthenticatedTemplate>
  );
};

export default ProfileEdit;

